import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Loader, Play, Search, Eye, Download } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

type Step = 'options' | 'test' | 'discover' | 'preview' | 'import' | 'done';

interface Connection {
  id: string;
  name: string;
  connector_id: string;
  status: string;
}

interface DiscoveryEntity {
  entity_type: string;
  record_count: number;
  sample_fields: string[];
}

interface ImportReport {
  started_at: string;
  completed_at: string;
  duration_seconds: number;
  entities: {
    entity_type: string;
    total: number;
    imported: number;
    skipped: number;
    failed: number;
    errors: { source_id: string; message: string }[];
  }[];
  dedup_stats: {
    matched: number;
    skipped: number;
    updated: number;
    created_new: number;
  };
}

const ENTITY_LABELS: Record<string, string> = {
  companies: 'Companies',
  contacts: 'Contacts',
  tickets: 'Tickets',
  time_entries: 'Time Entries',
  configurations: 'Configurations / CIs',
  contracts: 'Contracts',
  projects: 'Projects',
  products: 'Products',
  documents: 'Documents / Flexible Assets',
};

export default function ConnectorImportPage() {
  const { connectionId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('options');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connection, setConnection] = useState<Connection | null>(null);

  // Import options
  const [importOptions, setImportOptions] = useState({
    companies: true,
    contacts: true,
    tickets: false,
    time_entries: false,
    configurations: false,
    contracts: false,
    projects: false,
    products: false,
    documents: false,
  });
  const [dedupStrategy, setDedupStrategy] = useState<'skip' | 'update' | 'duplicate'>('skip');

  // State
  const [instanceId, setInstanceId] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [discovery, setDiscovery] = useState<DiscoveryEntity[]>([]);
  const [preview, setPreview] = useState<Record<string, { records: Record<string, unknown>[]; total: number }>>({});
  const [report, setReport] = useState<ImportReport | null>(null);

  useEffect(() => {
    if (connectionId) loadConnection();
  }, [connectionId]);

  async function loadConnection() {
    try {
      const data = await api.get<{ connections: Connection[] }>('/connections');
      const conn = data.connections.find(c => c.id === connectionId);
      if (conn) setConnection(conn);
    } catch {}
  }

  async function handleCreateAndTest() {
    if (!connectionId) return;
    setLoading(true);
    setError('');

    try {
      // Create connector instance
      const { instance } = await api.post<{ instance: { id: string } }>('/connector-instances', {
        connection_id: connectionId,
        import_options: importOptions,
        dedup_strategy: dedupStrategy,
      });
      setInstanceId(instance.id);

      // Test connection
      setStep('test');
      const result = await api.post<{ success: boolean; message: string }>(`/connector-instances/${instance.id}/test`);
      setTestResult(result);

      if (result.success) {
        // Auto-discover
        setStep('discover');
        const disc = await api.post<{ discovery: { entities: DiscoveryEntity[] } }>(`/connector-instances/${instance.id}/discover`);
        setDiscovery(disc.discovery.entities);
        setStep('preview');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function handlePreview() {
    if (!instanceId) return;
    setLoading(true);
    setError('');

    try {
      const result = await api.post<{ preview: Record<string, { records: Record<string, unknown>[]; total: number }> }>(`/connector-instances/${instanceId}/preview`);
      setPreview(result.preview);
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function handleImport() {
    if (!instanceId) return;
    setLoading(true);
    setError('');
    setStep('import');

    try {
      const result = await api.post<{ report: ImportReport }>(`/connector-instances/${instanceId}/import`);
      setReport(result.report);
      setStep('done');
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  }

  function toggleOption(key: keyof typeof importOptions) {
    setImportOptions(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const selectedCount = Object.values(importOptions).filter(Boolean).length;

  return (
    <div>
      <button onClick={() => navigate('/connections')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#6b7280', marginBottom: '1rem', padding: 0, fontSize: '0.875rem' }}>
        <ArrowLeft size={16} /> Back to Connections
      </button>

      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.25rem', color: '#111827' }}>
        Import from {connection?.name || 'Connector'}
      </h2>
      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.5rem' }}>
        {connection?.connector_id} connector
      </p>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['options', 'test', 'discover', 'preview', 'import', 'done'] as Step[]).map((s, i) => (
          <div key={s} style={{
            flex: 1, height: 4, borderRadius: 2,
            backgroundColor: (['options', 'test', 'discover', 'preview', 'import', 'done'].indexOf(step) >= i) ? ACCENT : '#e5e7eb',
          }} />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Step: Options */}
      {step === 'options' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>What would you like to import?</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {(Object.entries(ENTITY_LABELS) as [keyof typeof importOptions, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => toggleOption(key)}
                style={{
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  border: importOptions[key] ? `2px solid ${ACCENT}` : '1px solid #e5e7eb',
                  backgroundColor: importOptions[key] ? '#fff7ed' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.8125rem',
                  fontWeight: importOptions[key] ? 600 : 400,
                  color: importOptions[key] ? '#111827' : '#6b7280',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.5rem' }}>Duplicate Handling</h4>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {(['skip', 'update', 'duplicate'] as const).map(s => (
              <button
                key={s}
                onClick={() => setDedupStrategy(s)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: dedupStrategy === s ? `2px solid ${ACCENT}` : '1px solid #e5e7eb',
                  backgroundColor: dedupStrategy === s ? '#fff7ed' : '#fff',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: dedupStrategy === s ? 600 : 400,
                }}
              >
                {s === 'skip' ? 'Skip duplicates' : s === 'update' ? 'Update existing' : 'Create duplicate'}
              </button>
            ))}
          </div>

          <button onClick={handleCreateAndTest} disabled={selectedCount === 0 || loading} style={{ ...primaryBtnStyle, opacity: selectedCount > 0 ? 1 : 0.5 }}>
            {loading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={16} />}
            Connect & Discover ({selectedCount} entities)
          </button>
        </div>
      )}

      {/* Step: Test */}
      {step === 'test' && loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader size={32} color={ACCENT} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <p style={{ color: '#6b7280' }}>Testing connection...</p>
        </div>
      )}

      {step === 'test' && !loading && testResult && !testResult.success && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #fecaca', padding: '1.25rem', textAlign: 'center' }}>
          <XCircle size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 0.5rem', color: '#dc2626' }}>Connection Failed</h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>{testResult.message}</p>
          <button onClick={() => { setStep('options'); setError(''); }} style={{ ...secondaryBtnStyle, marginTop: '1rem' }}>
            Back to Settings
          </button>
        </div>
      )}

      {/* Step: Discover */}
      {step === 'discover' && loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Search size={32} color={ACCENT} style={{ marginBottom: '1rem' }} />
          <p style={{ color: '#6b7280' }}>Discovering available data...</p>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && !loading && (
        <div>
          {/* Discovery Results */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Data Discovery</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' }}>
              {discovery.map(e => (
                <div key={e.entity_type} style={{ padding: '0.75rem', borderRadius: '0.375rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: ACCENT }}>{e.record_count.toLocaleString()}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'capitalize' }}>{e.entity_type.replace('_', ' ')}s</div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview Data */}
          {Object.keys(preview).length > 0 && (
            <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem', overflow: 'auto' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Sample Records</h3>
              {Object.entries(preview).map(([entityType, data]) => (
                <div key={entityType} style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.5rem', textTransform: 'capitalize' }}>
                    {entityType.replace('_', ' ')}s ({data.total.toLocaleString()} total)
                  </h4>
                  {data.records.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          {Object.keys(data.records[0]).filter(k => !k.startsWith('_')).slice(0, 6).map(k => (
                            <th key={k} style={thStyle}>{k}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.records.slice(0, 5).map((record, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            {Object.entries(record).filter(([k]) => !k.startsWith('_')).slice(0, 6).map(([k, v]) => (
                              <td key={k} style={tdStyle}>{String(v || '')}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>No records found</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {Object.keys(preview).length === 0 && (
              <button onClick={handlePreview} disabled={loading} style={{ ...secondaryBtnStyle, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Eye size={16} /> Preview Sample Data
              </button>
            )}
            <button onClick={handleImport} disabled={loading} style={primaryBtnStyle}>
              {loading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={16} />}
              Start Import
            </button>
          </div>
        </div>
      )}

      {/* Step: Import in progress */}
      {step === 'import' && loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Loader size={48} color={ACCENT} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 0.5rem' }}>Importing Data...</h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>This may take a few minutes depending on the amount of data.</p>
        </div>
      )}

      {/* Step: Done */}
      {step === 'done' && report && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <CheckCircle size={48} color="#10b981" style={{ marginBottom: '0.75rem' }} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.25rem' }}>Import Complete</h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Completed in {report.duration_seconds}s
            </p>
          </div>

          {/* Per-entity breakdown */}
          <div style={{ marginBottom: '1.5rem' }}>
            {report.entities.map(e => (
              <div key={e.entity_type} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ flex: 1, fontWeight: 500, fontSize: '0.875rem', textTransform: 'capitalize' }}>{e.entity_type.replace('_', ' ')}s</div>
                <StatPill color="#10b981" label="Imported" value={e.imported} />
                <StatPill color="#6b7280" label="Skipped" value={e.skipped} />
                <StatPill color="#ef4444" label="Failed" value={e.failed} />
              </div>
            ))}
          </div>

          {/* Dedup stats */}
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem', fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Dedup: {report.dedup_stats.matched} matched, {report.dedup_stats.skipped} skipped, {report.dedup_stats.created_new} created new
          </div>

          {/* Errors */}
          {report.entities.some(e => e.errors.length > 0) && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.5rem', color: '#dc2626' }}>Errors</h4>
              {report.entities.filter(e => e.errors.length > 0).map(e => (
                <div key={e.entity_type}>
                  <p style={{ fontSize: '0.8rem', fontWeight: 500 }}>{e.entity_type}:</p>
                  <ul style={{ margin: '0.25rem 0', paddingLeft: '1.5rem' }}>
                    {e.errors.slice(0, 5).map((err, i) => (
                      <li key={i} style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                        {err.source_id && `[${err.source_id}] `}{err.message}
                      </li>
                    ))}
                    {e.errors.length > 5 && (
                      <li style={{ fontSize: '0.75rem', color: '#6b7280' }}>...and {e.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => navigate('/connections')} style={secondaryBtnStyle}>Back to Connections</button>
            <button onClick={() => { setStep('options'); setInstanceId(''); setDiscovery([]); setPreview({}); setReport(null); setError(''); }} style={primaryBtnStyle}>
              Import Again
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function StatPill({ color, label, value }: { color: string; label: string; value: number }) {
  if (value === 0) return null;
  return (
    <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '1rem', backgroundColor: color + '15', color, fontWeight: 500 }}>
      {value} {label}
    </span>
  );
}

const thStyle: React.CSSProperties = { textAlign: 'left', padding: '0.375rem 0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' };
const tdStyle: React.CSSProperties = { padding: '0.375rem 0.5rem', fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const primaryBtnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', backgroundColor: '#f97316', color: '#fff', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' };
const secondaryBtnStyle: React.CSSProperties = { padding: '0.5rem 1.25rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' };
